import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dropdown,
  Form,
  Message,
  Segment,
  Table,
} from "semantic-ui-react";
import { useMediaQuery } from "react-responsive";

import {
  TakeoutDiscountRule,
  TakeoutDiscountRulePayload,
  TakeoutDiscountRuleType,
} from "domain/repository/takeout";

type DiscountRuleRow = {
  min_amount: string;
  max_amount: string; // 空文字はnull扱い
  type: TakeoutDiscountRuleType;
  value: string; // free_shipping は空文字固定でOK
};

type Props = {
  initialRules: TakeoutDiscountRule[];
  onSubmit: (rules: TakeoutDiscountRulePayload[]) => Promise<void>;
};

const typeOptions = [
  { key: "percent", value: "percent", text: "%割引" },
  { key: "amount", value: "amount", text: "円割引" },
  { key: "free_shipping", value: "free_shipping", text: "送料無料" },
];

/**
 * APIで取得した割引ルール（number/null）を、フォームの入力状態（string/空欄）へ変換する。
 *
 * NOTE:
 * - `<input>` は常に文字列で扱われるため、数値も string に寄せて state 管理する。
 * - 未設定（null）はフォーム上で空欄として見せたいので "" にする。
 * - free_shipping の value は null の想定だが、フォームでは入力欄を無効化して空欄に寄せる。
 */
const responseToRows = (rules: TakeoutDiscountRule[]): DiscountRuleRow[] => {
  return (rules || []).map((r) => ({
    min_amount: (r.min_amount ?? "").toString(),
    max_amount: r.max_amount == null ? "" : r.max_amount.toString(),
    type: r.type,
    value: r.value == null ? "" : r.value.toString(),
  }));
};

/**
 * フォームの入力状態（string/空欄）を、API更新用の payload へ変換する。
 *
 * NOTE:
 * - バリデーションは更新時に getClientValidationError で必須未入力を検知し、
 *   それ以外はサーバ側（Normalizer）を正とする。
 *   ここでは「空欄→null」など、送信フォーマットの整形のみ行う。
 * - サーバ側は Integer("5000") のように文字列も数値として解釈できるため、
 *   フロントは数値化せず（入力そのまま）送る。
 * - エラー時はサーバが返す `{ message: "..." }` をそのまま表示する。
 */
const rowsToUpdatePayload = (
  rows: DiscountRuleRow[]
): TakeoutDiscountRulePayload[] => {
  return (rows || []).map((r) => {
    const min = (r.min_amount ?? "").toString().trim();
    const max = (r.max_amount ?? "").toString().trim();
    const value = (r.value ?? "").toString().trim();

    return {
      // NOTE: 空欄は null にしてサーバ側の ValidationError(message) をそのまま使う
      min_amount: min === "" ? null : min,
      max_amount: max === "" ? null : max,
      type: r.type,
      value: r.type === "free_shipping" ? null : value === "" ? null : value,
    };
  });
};

const getClientValidationError = (rows: DiscountRuleRow[]): string => {
  for (const row of rows) {
    const min = (row.min_amount ?? "").toString().trim();
    const value = (row.value ?? "").toString().trim();
    if (min === "") {
      return "下限金額（円）が未入力です。";
    }
    if (row.type !== "free_shipping" && value === "") {
      return "値が未入力です。";
    }
  }
  return "";
};

const TakeoutDiscountRuleForm: React.FC<Props> = ({
  initialRules,
  onSubmit,
}) => {
  // NOTE: テーブル表示は幅が狭いと崩れやすいので、1020px以下はカード（縦並び）へ切り替える
  const isNarrow = useMediaQuery({ maxWidth: 1020 });
  const [rows, setRows] = useState<DiscountRuleRow[]>(() =>
    responseToRows(initialRules)
  );
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    setRows(responseToRows(initialRules));
  }, [initialRules]);

  const addRow = useCallback(() => {
    setRows((prev) => [
      ...prev,
      { min_amount: "", max_amount: "", type: "percent", value: "" },
    ]);
  }, []);

  const removeRow = useCallback((index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateRow = useCallback(
    (index: number, patch: Partial<DiscountRuleRow>) => {
      setRows((prev) =>
        prev.map((r, i) => {
          if (i !== index) return r;
          const next = { ...r, ...patch };
          // free_shipping の場合は value を空に寄せる
          if (next.type === "free_shipping") {
            next.value = "";
          }
          return next;
        })
      );
    },
    []
  );

  const canSubmit = true;

  const handleSubmit = useCallback(async () => {
    try {
      const clientError = getClientValidationError(rows);
      if (clientError) {
        alert(clientError);
        return;
      }
      setSubmitting(true);
      await onSubmit(rowsToUpdatePayload(rows));
    } catch (e: any) {
      // サーバ側 ValidationError の message を最優先で表示する
      const message =
        e?.response?.data?.message || e?.message || "更新に失敗しました";
      alert(message);
    } finally {
      setSubmitting(false);
    }
  }, [onSubmit, rows]);

  return (
    <Segment>
      <Form>
        <Message
          info
          content="割引は「商品小計」に対して適用されます（送料・代引き手数料は対象外）。%割引の端数は切り捨てです。"
        />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button type="button" size="small" content="追加" onClick={addRow} />
        </div>
        {isNarrow ? (
          <div>
            {rows.length === 0 ? (
              <div
                style={{
                  border: "1px solid rgba(34, 36, 38, 0.15)",
                  borderRadius: 4,
                  padding: "0.75rem",
                }}
              >
                未設定（割引なし）
              </div>
            ) : (
              rows.map((row, idx) => (
                <div
                  key={idx}
                  style={{
                    border: "1px solid rgba(34, 36, 38, 0.15)",
                    borderRadius: 4,
                    padding: "0.75rem",
                    marginTop: "0.75rem",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      type="button"
                      size="small"
                      icon="trash"
                      title="削除"
                      aria-label="削除"
                      onClick={() => removeRow(idx)}
                    />
                  </div>
                  <Form.Input
                    fluid
                    label="下限金額（円）"
                    value={row.min_amount}
                    placeholder="例: 5000"
                    type="number"
                    min="0"
                    onChange={(e) =>
                      updateRow(idx, { min_amount: e.target.value })
                    }
                  />
                  <Form.Input
                    fluid
                    label="上限金額（円）"
                    value={row.max_amount}
                    placeholder="未指定=上限なし"
                    type="number"
                    min="0"
                    onChange={(e) =>
                      updateRow(idx, { max_amount: e.target.value })
                    }
                  />
                  <Form.Field>
                    <label>割引種別</label>
                    <Dropdown
                      fluid
                      selection
                      options={typeOptions}
                      value={row.type}
                      onChange={(_, data) =>
                        updateRow(idx, {
                          type: data.value as TakeoutDiscountRuleType,
                        })
                      }
                    />
                  </Form.Field>
                  <Form.Input
                    fluid
                    label="値"
                    value={row.value}
                    placeholder={
                      row.type === "percent"
                        ? "例: 5"
                        : row.type === "amount"
                        ? "例: 1000"
                        : "-"
                    }
                    disabled={row.type === "free_shipping"}
                    type="number"
                    min={
                      row.type === "percent"
                        ? "1"
                        : row.type === "amount"
                        ? "1"
                        : undefined
                    }
                    max={row.type === "percent" ? "100" : undefined}
                    onChange={(e) => updateRow(idx, { value: e.target.value })}
                  />
                </div>
              ))
            )}
          </div>
        ) : (
          <Table celled style={{ tableLayout: "fixed", width: "100%" }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell style={{ width: "22%" }}>
                  下限金額（円）
                </Table.HeaderCell>
                <Table.HeaderCell style={{ width: "22%" }}>
                  上限金額（円）
                </Table.HeaderCell>
                <Table.HeaderCell style={{ width: "22%" }}>
                  割引種別
                </Table.HeaderCell>
                <Table.HeaderCell style={{ width: "30%" }}>値</Table.HeaderCell>
                <Table.HeaderCell collapsing style={{ width: 56 }} />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={5}>未設定（割引なし）</Table.Cell>
                </Table.Row>
              )}
              {rows.map((row, idx) => (
                <Table.Row key={idx}>
                  <Table.Cell>
                    <Form.Input
                      fluid
                      value={row.min_amount}
                      placeholder="例: 5000"
                      type="number"
                      min="0"
                      onChange={(e) =>
                        updateRow(idx, { min_amount: e.target.value })
                      }
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Form.Input
                      fluid
                      value={row.max_amount}
                      placeholder="未指定=上限なし"
                      type="number"
                      min="0"
                      onChange={(e) =>
                        updateRow(idx, { max_amount: e.target.value })
                      }
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Dropdown
                      fluid
                      selection
                      options={typeOptions}
                      value={row.type}
                      onChange={(_, data) =>
                        updateRow(idx, {
                          type: data.value as TakeoutDiscountRuleType,
                        })
                      }
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Form.Input
                      fluid
                      value={row.value}
                      placeholder={
                        row.type === "percent"
                          ? "例: 5"
                          : row.type === "amount"
                          ? "例: 1000"
                          : "-"
                      }
                      disabled={row.type === "free_shipping"}
                      type="number"
                      min={
                        row.type === "percent"
                          ? "1"
                          : row.type === "amount"
                          ? "1"
                          : undefined
                      }
                      max={row.type === "percent" ? "100" : undefined}
                      onChange={(e) =>
                        updateRow(idx, { value: e.target.value })
                      }
                    />
                  </Table.Cell>
                  <Table.Cell
                    collapsing
                    textAlign="center"
                    style={{ width: 56 }}
                  >
                    <Button
                      type="button"
                      size="small"
                      icon="trash"
                      title="削除"
                      aria-label="削除"
                      onClick={() => removeRow(idx)}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <Button
            color="blue"
            content="更新"
            type="button"
            disabled={!canSubmit || submitting}
            loading={submitting}
            onClick={handleSubmit}
          />
        </div>
      </Form>
    </Segment>
  );
};

export default TakeoutDiscountRuleForm;
